import type {Mapper} from '@automapper/core';
import type {BaseHttpCodes, DownloadColumn, IDownloadByLinkService} from '@igp/shared/api';
import {ServerResponseStatus} from '@igp/shared/api';
import type {WeeklySummaryExport} from '@igp/shared/contract/pam';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import type {IEntityReadService} from '@igp/shared/redux/entity';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {negativeCarryoverReportMappingKeys} from './mapper';
import type {NegativeCarryoverReportService} from './service';
import type {
    AgentNegativeCarryoverReportExtended,
    NegativeCarryoverReportDownloadFields,
    NegativeCarryoverReportViewModelKeys,
    NegativeCarryoverWeeklySummaryDownloadFields,
    NegativeCarryoverWeeklySummaryViewModelKeys,
} from './types';
import {NegativeCarryoverReportServiceTypes} from './types';

@injectable()
export class NegativeCarryoverReportDownloadClientStrategy<TCodes extends BaseHttpCodes | string> extends EntityDownloadClientStrategy<
    NegativeCarryoverReportViewModelKeys,
    EntityType
> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(NegativeCarryoverReportServiceTypes.Service) service: NegativeCarryoverReportService<TCodes>
    ) {
        super(mapper, service, EntityType.NegaitveCarryoverReport, {
            viewModelKeys: negativeCarryoverReportMappingKeys.viewModelKeys,
            queryFields: negativeCarryoverReportMappingKeys.queryKeys,
            model: negativeCarryoverReportMappingKeys.serverModel,
            downloadModel: negativeCarryoverReportMappingKeys.downloadModel,
        });
    }

    filterItems(items: AgentNegativeCarryoverReportExtended[]): AgentNegativeCarryoverReportExtended[] {
        return items.filter(item => !item.is_general);
    }
}

@injectable()
export class NegativeCarryoverReportDownloadServerStrategy implements IDownloadStrategy<NegativeCarryoverReportViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<NegativeCarryoverReportDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(NegativeCarryoverReportServiceTypes.Service)
        service: IDownloadByLinkService<NegativeCarryoverReportDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<NegativeCarryoverReportViewModelKeys>
    ): Observable<DownloadStrategyResponse<NegativeCarryoverReportViewModelKeys>> {
        const columns: DownloadColumn<NegativeCarryoverReportDownloadFields>[] = request.keys?.map<
            DownloadColumn<NegativeCarryoverReportDownloadFields>
        >(k => {
            const queryField = this._mapper.map<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportDownloadFields>(
                k,
                negativeCarryoverReportMappingKeys.viewModelKeys,
                negativeCarryoverReportMappingKeys.downloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}

@injectable()
export class NegativeCarryoverWeeklySummaryDownloadClientStrategy extends EntityDownloadClientStrategy<
    NegativeCarryoverWeeklySummaryViewModelKeys,
    EntityType
> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(NegativeCarryoverReportServiceTypes.WeeklySummaryService) service: IEntityReadService<EntityType, WeeklySummaryExport>
    ) {
        super(mapper, service, EntityType.NegativeCarryoverWeeklySummary, {
            viewModelKeys: negativeCarryoverReportMappingKeys.weeklySummaryViewModelKeys,
            queryFields: negativeCarryoverReportMappingKeys.weeklySummaryQueryKeys,
            model: negativeCarryoverReportMappingKeys.weeklySummaryServerModel,
            downloadModel: negativeCarryoverReportMappingKeys.weeklySummaryDownloadModel,
        });
    }
}

@injectable()
export class NegativeCarryoverWeeklySummaryDownloadServerStrategy
    implements IDownloadStrategy<NegativeCarryoverWeeklySummaryViewModelKeys>
{
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<NegativeCarryoverWeeklySummaryDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(NegativeCarryoverReportServiceTypes.WeeklySummaryService)
        service: IDownloadByLinkService<NegativeCarryoverWeeklySummaryDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<NegativeCarryoverWeeklySummaryViewModelKeys>
    ): Observable<DownloadStrategyResponse<NegativeCarryoverWeeklySummaryViewModelKeys>> {
        const columns: DownloadColumn<NegativeCarryoverWeeklySummaryDownloadFields>[] = request.keys?.map<
            DownloadColumn<NegativeCarryoverWeeklySummaryDownloadFields>
        >(k => {
            const queryField = this._mapper.map<NegativeCarryoverWeeklySummaryViewModelKeys, NegativeCarryoverWeeklySummaryDownloadFields>(
                k,
                negativeCarryoverReportMappingKeys.weeklySummaryViewModelKeys,
                negativeCarryoverReportMappingKeys.weeklySummaryDownloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
